import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

/**
 * Response from POST /api/auth/password-reset/request
 * Always returns same structure to prevent email enumeration
 */
export interface PasswordResetRequestResponse {
  success: boolean;
  message: string;
}

/**
 * Response from POST /api/auth/password-reset/validate
 */
export interface ValidateTokenResponse {
  valid: boolean;
  resetRequestId?: string;
  pinHint?: string; // e.g., "***3" - masked hint of last digit
  expiresAt?: string;
  errorCode?: string;
}

/**
 * Response from POST /api/auth/password-reset/verify-pin
 */
export interface VerifyPinResponse {
  verified: boolean;
  resetSessionToken?: string;
  attemptsRemaining?: number;
  errorCode?: string;
}

/**
 * Response from POST /api/auth/password-reset/complete
 */
export interface CompleteResetResponse {
  success: boolean;
  message?: string;
  errorCode?: string;
}

/**
 * Secure Password Reset Service
 *
 * Implements the Token + PIN password reset flow:
 * 1. User requests reset → receives email with link (token) and PIN
 * 2. User clicks link → token is validated
 * 3. User enters PIN → PIN is verified server-side
 * 4. User enters new password → reset is completed
 *
 * Security notes:
 * - PIN is NEVER returned by the API (only in email)
 * - Token is only stored as hash on server
 * - Neutral responses prevent email enumeration
 * - Rate limiting and max attempts are enforced server-side
 *
 * Target users: External users only (kprus table), not internal RSV users
 */
@Injectable({
  providedIn: 'root',
})
export class PasswordResetService {
  private http = inject(HttpClient);

  private readonly baseUrl = `${environment.apiUrl}/api/auth/password-reset`;

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control': 'no-store, must-revalidate',
    }),
  };

  // State for multi-step flow
  private _currentToken: string | null = null;
  private _resetRequestId: string | null = null;
  private _resetSessionToken: string | null = null;
  private _pinHint: string | null = null;

  // Observable state for components
  private _flowState = new BehaviorSubject<'idle' | 'token-validated' | 'pin-verified'>('idle');
  public flowState$ = this._flowState.asObservable();

  /**
   * Step 1: Request a password reset
   * Always returns success to prevent email enumeration
   */
  requestReset(email: string): Observable<PasswordResetRequestResponse> {
    return this.http.post<PasswordResetRequestResponse>(`${this.baseUrl}/request`, { email }, this.httpOptions).pipe(
      map(response => response),
      catchError(error => {
        console.error('Password reset request failed:', error);
        // Always return success to prevent enumeration
        return of({
          success: true,
          message: 'If the email exists, a reset link has been sent.',
        });
      })
    );
  }

  /**
   * Step 2: Validate the token from the reset link
   * Called when user opens the reset link from email
   */
  validateToken(token: string): Observable<ValidateTokenResponse> {
    console.log('🔑 [PasswordReset] Validating token:', token);
    console.log('🔑 [PasswordReset] API URL:', `${this.baseUrl}/validate`);

    return this.http.post<ValidateTokenResponse>(`${this.baseUrl}/validate`, { token }, this.httpOptions).pipe(
      map(response => {
        console.log('🔑 [PasswordReset] Validate response:', response);
        if (response.valid) {
          this._currentToken = token;
          this._resetRequestId = response.resetRequestId ?? null;
          this._pinHint = response.pinHint ?? null;
          this._flowState.next('token-validated');
        }
        return response;
      }),
      catchError(error => {
        console.error('🔑 [PasswordReset] Token validation failed:', error);
        console.error('🔑 [PasswordReset] Error status:', error?.status);
        console.error('🔑 [PasswordReset] Error message:', error?.message);
        return of({
          valid: false,
          errorCode: 'VALIDATION_ERROR',
        });
      })
    );
  }

  /**
   * Step 3: Verify the PIN (from email)
   * PIN is verified server-side, never exposed via API
   */
  verifyPin(pin: string): Observable<VerifyPinResponse> {
    if (!this._currentToken) {
      return of({
        verified: false,
        errorCode: 'NO_TOKEN',
      });
    }

    return this.http
      .post<VerifyPinResponse>(
        `${this.baseUrl}/verify-pin`,
        {
          token: this._currentToken,
          pin,
        },
        this.httpOptions
      )
      .pipe(
        map(response => {
          if (response.verified && response.resetSessionToken) {
            this._resetSessionToken = response.resetSessionToken;
            this._flowState.next('pin-verified');
          }
          return response;
        }),
        catchError(error => {
          console.error('PIN verification failed:', error);
          return of({
            verified: false,
            errorCode: 'VERIFICATION_ERROR',
          });
        })
      );
  }

  /**
   * Step 4: Complete the password reset with new password
   */
  completeReset(newPassword: string): Observable<CompleteResetResponse> {
    if (!this._resetSessionToken) {
      return of({
        success: false,
        errorCode: 'NO_SESSION',
      });
    }

    return this.http
      .post<CompleteResetResponse>(
        `${this.baseUrl}/complete`,
        {
          resetSessionToken: this._resetSessionToken,
          newPassword,
        },
        this.httpOptions
      )
      .pipe(
        map(response => {
          if (response.success) {
            this.resetState();
          }
          return response;
        }),
        catchError(error => {
          console.error('Password reset completion failed:', error);
          return of({
            success: false,
            errorCode: 'COMPLETION_ERROR',
          });
        })
      );
  }

  /**
   * Get the PIN hint (e.g., "***3") for display
   */
  get pinHint(): string | null {
    return this._pinHint;
  }

  /**
   * Check if we have a valid token in the flow
   */
  get hasValidToken(): boolean {
    return this._currentToken !== null;
  }

  /**
   * Check if PIN has been verified
   */
  get isPinVerified(): boolean {
    return this._resetSessionToken !== null;
  }

  /**
   * Reset all state - call on completion or cancellation
   */
  resetState(): void {
    this._currentToken = null;
    this._resetRequestId = null;
    this._resetSessionToken = null;
    this._pinHint = null;
    this._flowState.next('idle');
  }
}
